import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Point } from '../Geometry';
import { LineDirection, LineMeasurement } from '../../../data/Measurements/Measurement';
import { useMeasurement } from '../../../providers/NewMeasurementProvider';
import { useCanvasViewport } from '../CanvasViewportContext';
import { set } from 'lodash';
import { useFloating } from '@floating-ui/react-dom';

// MARK: Types 

type BaseRepresentationProps = {
}

type LineStyle = {
  /**
   * The inner color of the line.
   */
  innerColor: string;

  /**
   * The outer color of the line.
   */
  outerColor: string;

  /**
   * The width of the line.
   */
  width: number;

  /**
   * The radius of the anchor.
   */
  anchorRadius: number;
}

type LineMeasurementRepresentationProps = BaseRepresentationProps & {
  /**
   * The line measurement to render.
   */
  measurement: LineMeasurement;

  /**
   * The style of the line.
   */
  style?: LineStyle;
}

type NotDraggingState = {
  isDragging: false;
}

type DraggingInProgressState = {
  isDragging: true;

  dragStart: Point;
  dragPosition: Point;

  anchor: "start" | "end";
}

type DraggingState = NotDraggingState | DraggingInProgressState;

const DEFAULT_LINE_STYLE: LineStyle = {
  innerColor: "rgba(124, 85, 202, 1.0)",
  outerColor: "rgba(255, 255, 255, 1.0)",
  width: 2,
  anchorRadius: 20
}

// MARK: Component 

export function LineMeasurementRepresentation({ measurement, style }: LineMeasurementRepresentationProps) {
  const innerColor = style?.innerColor ?? DEFAULT_LINE_STYLE.innerColor;
  const outerColor = style?.outerColor ?? DEFAULT_LINE_STYLE.outerColor;
  const width = style?.width ?? DEFAULT_LINE_STYLE.width;
  const anchorRadius = style?.anchorRadius ?? DEFAULT_LINE_STYLE.anchorRadius;

  const { updateMeasurementAsync } = useMeasurement();
  const { setFocusedKeys, setPageMousePosition, canvasMousePosition, realToVirtual, virtualToReal,
    measurementKeyThatWasJustCreated, openMeasurementContextMenu, measurementContextMenu,
    closeMeasurementContextMenu, dragAction,
    setMeasurementKeyBeingDragged } = useCanvasViewport();

  // MARK: Hovering

  const [isHovering, setIsHovering] = useState(false);

  // MARK: Dragging

  /**
   * Controls the dragging state of the line.
   */
  const [draggingState, setDraggingState] = useState<DraggingState>({ isDragging: false });

  /**
   * Starts the dragging of the line.
   */
  const startDrag = useCallback((anchor: "start" | "end") => {
    if (draggingState.isDragging) {
      console.warn("Tried to start dragging while already dragging.");

      return;
    }

    if (dragAction === 'inactive') {
      return;
    }

    const dragStart = anchor === "start" ? measurement.startPosition : measurement.endPosition;
    const dragStartOnCanvas = virtualToReal(dragStart);

    setDraggingState({
      isDragging: true,
      dragStart: dragStartOnCanvas,
      dragPosition: dragStartOnCanvas,
      anchor
    });

    setFocusedKeys([measurement.key]);
    setMeasurementKeyBeingDragged(measurement.key);
  }, [draggingState.isDragging, dragAction, measurement.startPosition, measurement.endPosition, measurement.key, virtualToReal, setFocusedKeys, setMeasurementKeyBeingDragged]);

  /**
   * Updates the dragging of the line.
   */
  const updateDrag = useCallback((event: React.MouseEvent<SVGCircleElement>) => {
    setPageMousePosition({ x: event.clientX, y: event.clientY });

    if (!draggingState.isDragging) {
      return;
    }

    const correctedPosition = correctDragPositionConsideringLineDirection(canvasMousePosition, draggingState.dragStart, measurement.direction);

    setDraggingState({
      ...draggingState,
      dragPosition: correctedPosition
    });

    console.log(`drag: update ${draggingState.dragPosition.x}, ${draggingState.dragPosition.y}`);
  }, [setPageMousePosition, draggingState, canvasMousePosition, measurement.direction]);

  /**
   * Ends the dragging of the line.
   */
  const endDrag = useCallback(() => {
    if (!draggingState.isDragging) {
      return;
    }

    console.log(`drag: end`);

    const finalDragPositionInVirtualCoordinates = realToVirtual(draggingState.dragPosition);

    const newStartPosition = draggingState.anchor === "start" ? finalDragPositionInVirtualCoordinates : measurement.startPosition;
    const newEndPosition = draggingState.anchor === "end" ? finalDragPositionInVirtualCoordinates : measurement.endPosition;

    updateMeasurementAsync({
      ...measurement,
      startPosition: newStartPosition,
      endPosition: newEndPosition
    })
      .then(() => {
        console.log("Measurement updated successfully.");
      })
      .catch((error) => {
        console.error("Failed to update measurement.", error);
      })
      .finally(() => {
        setDraggingState({ isDragging: false });
        setFocusedKeys(null);
        setMeasurementKeyBeingDragged(null);
      });
  }, [draggingState, realToVirtual, measurement, updateMeasurementAsync, setFocusedKeys, setMeasurementKeyBeingDragged]);

  // MARK: Mouse Events

  const onMouseDown = useCallback((event: React.MouseEvent<SVGCircleElement>) => {
    if (event.button === 0) {
      const target = event.target as SVGCircleElement;
      const anchor = target.getAttribute("drag-anchor") as "start" | "end";

      startDrag(anchor);
    }
  }, [startDrag]);

  const onMouseEnter = useCallback(() => {
    setIsHovering(true);
  }, [setIsHovering]);

  const onMouseLeave = useCallback(() => {
    if (draggingState.isDragging) {
      endDrag();
    }

    setIsHovering(false);
    setMeasurementKeyBeingDragged(null);
  }, [draggingState, setMeasurementKeyBeingDragged, endDrag]);

  const onContextMenu = useCallback((event: React.MouseEvent<SVGCircleElement>) => {
    event.preventDefault();

    if (measurementContextMenu.active) {
      closeMeasurementContextMenu();
    } else {
      openMeasurementContextMenu(measurement, canvasMousePosition);
    }
  }, [measurementContextMenu.active, closeMeasurementContextMenu, openMeasurementContextMenu, measurement, canvasMousePosition]);

  /**
   * Automatically starts the dragging of the line when the measurement is created.
   */
  const shouldAutomaticallyStartDragOnEndAnchor = useMemo(() => {
    return measurementKeyThatWasJustCreated === measurement.key;
  }, [measurementKeyThatWasJustCreated, measurement]);

  useEffect(() => {
    if (shouldAutomaticallyStartDragOnEndAnchor) {
      startDrag("end");
    }
  }, [shouldAutomaticallyStartDragOnEndAnchor, startDrag]);

  // MARK: Rendering

  const startPosOnCanvas = useMemo(() => {
    return virtualToReal(measurement.startPosition)
  }, [virtualToReal, measurement]);

  const endPosOnCanvas = useMemo(() => {
    return virtualToReal(measurement.endPosition)
  }, [virtualToReal, measurement]);

  const startPositionToUse = useMemo(() => {
    return draggingState.isDragging && draggingState.anchor === "start" ? draggingState.dragPosition : startPosOnCanvas;
  }, [draggingState, startPosOnCanvas]);

  const endPositionToUse = useMemo(() => {
    return draggingState.isDragging && draggingState.anchor === "end" ? draggingState.dragPosition : endPosOnCanvas;
  }, [draggingState, endPosOnCanvas]);

  const angle = Math.atan2(endPositionToUse.y - startPositionToUse.y, endPositionToUse.x - startPositionToUse.x);

  const perpLength = 20;
  const perpAngle = angle + Math.PI / 2;

  const interactionAreaClassName = useMemo(() => {
    return draggingState.isDragging ? "z-100 cursor-none user-select-none" : "cursor-grab";
  }, [draggingState]);

  return (
    <>
      {/* Start Interaction Circle */}
      <circle
        className={interactionAreaClassName}
        pointerEvents={'auto'}
        cx={startPositionToUse.x}
        cy={startPositionToUse.y}
        r={anchorRadius + (draggingState.isDragging && draggingState.anchor === "start" ? 100 : 4)}
        fill={outerColor}
        opacity={isHovering ? 0.1 : 0}
        onMouseDown={onMouseDown}
        onMouseMove={updateDrag}
        onMouseUp={endDrag}
        onMouseLeave={onMouseLeave}
        onMouseEnter={onMouseEnter}
        onContextMenu={onContextMenu}
        drag-anchor="start"
      />

      {/* Start Highlight Circle */}
      <circle
        cx={startPositionToUse.x}
        cy={startPositionToUse.y}
        r={anchorRadius}
        fill={innerColor}
        opacity={isHovering ? 0.3 : 0}
      />

      {/* End Interaction Circle */}
      <circle
        className={interactionAreaClassName}
        pointerEvents={'auto'}
        cx={endPositionToUse.x}
        cy={endPositionToUse.y}
        r={anchorRadius + (draggingState.isDragging && draggingState.anchor === "end" ? 100 : 4)}
        fill={outerColor}
        opacity={isHovering ? 0.1 : 0}
        onMouseDown={onMouseDown}
        onMouseMove={updateDrag}
        onMouseUp={endDrag}
        onMouseLeave={onMouseLeave}
        onMouseEnter={onMouseEnter}
        onContextMenu={onContextMenu}
        drag-anchor="end"
      />

      {/* End Highlight Circle */}
      <circle
        cx={endPositionToUse.x}
        cy={endPositionToUse.y}
        r={anchorRadius}
        fill={innerColor}
        opacity={isHovering ? 0.3 : 0}
      />

      <line
        x1={startPositionToUse.x}
        y1={startPositionToUse.y}
        x2={endPositionToUse.x}
        y2={endPositionToUse.y}
        stroke={outerColor}
        strokeWidth={width + 2}
      />

      <line
        x1={startPositionToUse.x}
        y1={startPositionToUse.y}
        x2={endPositionToUse.x}
        y2={endPositionToUse.y}
        stroke={innerColor}
        strokeWidth={width}
      />

      <line
        x1={startPositionToUse.x + Math.cos(perpAngle) * perpLength}
        y1={startPositionToUse.y + Math.sin(perpAngle) * perpLength}
        x2={startPositionToUse.x - Math.cos(perpAngle) * perpLength}
        y2={startPositionToUse.y - Math.sin(perpAngle) * perpLength}
        stroke={outerColor}
        strokeWidth={width + 2}
        opacity={isHovering ? 1 : 0}
      />

      <line
        x1={startPositionToUse.x + Math.cos(perpAngle) * perpLength}
        y1={startPositionToUse.y + Math.sin(perpAngle) * perpLength}
        x2={startPositionToUse.x - Math.cos(perpAngle) * perpLength}
        y2={startPositionToUse.y - Math.sin(perpAngle) * perpLength}
        stroke={innerColor}
        strokeWidth={width}
        opacity={isHovering ? 1 : 0}
      />

      <line
        x1={endPositionToUse.x + Math.cos(perpAngle) * perpLength}
        y1={endPositionToUse.y + Math.sin(perpAngle) * perpLength}
        x2={endPositionToUse.x - Math.cos(perpAngle) * perpLength}
        y2={endPositionToUse.y - Math.sin(perpAngle) * perpLength}
        stroke={outerColor}
        strokeWidth={width + 2}
        opacity={isHovering ? 1 : 0}
      />

      <line
        x1={endPositionToUse.x + Math.cos(perpAngle) * perpLength}
        y1={endPositionToUse.y + Math.sin(perpAngle) * perpLength}
        x2={endPositionToUse.x - Math.cos(perpAngle) * perpLength}
        y2={endPositionToUse.y - Math.sin(perpAngle) * perpLength}
        stroke={innerColor}
        strokeWidth={width}
        opacity={isHovering ? 1 : 0}
      />
    </>
  );
}

// MARK: Utils

/**
 * Corrects the drag position considering the line direction.
 * @param position The position to correct.
 * @param direction The direction of the line.
 */
function correctDragPositionConsideringLineDirection(
  currentPosition: Point,
  initialPosition: Point,
  direction: LineDirection
): Point {
  switch (direction) {
    case "free":
      return currentPosition;
    case "horizontal":
      return { x: currentPosition.x, y: initialPosition.y };
    case "vertical":
      return { x: initialPosition.x, y: currentPosition.y };
  }
}